import { useEffect, useState } from 'react'
import type { SurveyLinkDto } from '@/shared/api/types'
import { listSurveyLinks } from './api'
import { linkTypeLabel } from './vocab'

/**
 * Putting the poll's cross-module links ONTO a question or an option.
 *
 * ## The gap this closes
 *
 * `QuestionRequest` has carried `linkedEntityType` and `linkedEntityRef` — on
 * the question and on every option — since the instrument was built, and
 * `QuestionDialog` faithfully round-tripped both. Nothing anywhere could SET
 * them. The draft's own comment said the link "is chosen in the Builder, two
 * panels away"; it was not chosen in the Builder either, and the only way an
 * option ever acquired one was a seeded fixture.
 *
 * The consequence was quiet and complete: `SurveyEstimateService.pushCandidateOverlays`
 * only pushes a weighted share onto a candidate whose OPTION links to them, and
 * `SurveyDossierContributor` only finds a poll for a candidate the same way. A
 * ballot question authored through this UI linked to nobody, so neither ever
 * fired for a real poll.
 *
 * ## Why the options are the poll's declared links and not a free search
 *
 * A question is about something the POLL is about. Sourcing this picker from
 * `GET /surveys/{id}/links` means step 3 of the configurator is what populates
 * step 4, the two steps have a reason to be in that order, and the set of things
 * a poll measures is declared in exactly one place.
 *
 * It also inherits step 3's narrowing for free: those links were themselves
 * chosen from a server-narrowed picker, so a Langata poll cannot reach a Mombasa
 * governor here either — without this file re-implementing the seat rule, which
 * would be the second copy of it.
 *
 * A link this picker cannot offer is added on step 3. That is one more click for
 * the rare case, and it is the reason the common case is a three-item list
 * instead of every candidate in the country.
 */

export interface LinkChoice {
  typeCode: string
  ref: string
  label: string
  secondaryLabel: string | null
  resolved: boolean
}

export interface SurveyLinkChoices {
  choices: LinkChoice[]
  loading: boolean
  /** True once the read has finished and the poll has declared nothing. */
  empty: boolean
}

/**
 * The poll's links, as picker choices.
 *
 * Read once per open dialog rather than held in a module cache: unlike the
 * vocabulary, this list CHANGES during a session — somebody adds a candidate on
 * step 3 and comes straight back to step 4 — and a cache would serve them the
 * list from before their own edit.
 */
export function useSurveyLinkChoices(pollId: string | null, active: boolean): SurveyLinkChoices {
  const [links, setLinks] = useState<SurveyLinkDto[] | null>(null)

  useEffect(() => {
    if (!pollId || !active) { setLinks(null); return }
    let alive = true
    listSurveyLinks(pollId)
      .then((rows) => { if (alive) setLinks(rows) })
      // A failure leaves the picker saying "nothing declared", which is wrong but
      // harmless: the field is optional, and the alternative is a dialog that
      // cannot be used to write a question because a side panel would not load.
      .catch(() => { if (alive) setLinks([]) })
    return () => { alive = false }
  }, [pollId, active])

  return {
    choices: (links ?? []).map((l) => ({
      typeCode: l.typeCode,
      ref: l.ref,
      label: l.label ?? l.ref,
      secondaryLabel: l.secondaryLabel,
      resolved: l.resolved,
    })),
    loading: links === null && !!pollId && active,
    empty: links !== null && links.length === 0,
  }
}

/** `TYPE:ref`, so one `<select>` can carry a pair. Never sent to the server. */
const encode = (typeCode: string | null, ref: string | null) =>
  (typeCode && ref ? `${typeCode}:${ref}` : '')

export function decodeLinkValue(value: string): { typeCode: string | null; ref: string | null } {
  const at = value.indexOf(':')
  if (at < 0) return { typeCode: null, ref: null }
  return { typeCode: value.slice(0, at), ref: value.slice(at + 1) }
}

/**
 * One select, for a question or for an option row.
 *
 * The pair is encoded into the value because the server takes a type AND a ref
 * and a picker that only sent the ref would make the type a guess. Grouped by
 * type so a poll linked to four candidates and a programme reads as two lists
 * rather than five rows.
 */
export function LinkedEntitySelect({
  value, onChange, links, disabled, compact, ariaLabel,
}: {
  value: { typeCode: string | null; ref: string | null }
  onChange: (next: { typeCode: string | null; ref: string | null }) => void
  links: SurveyLinkChoices
  disabled?: boolean
  compact?: boolean
  ariaLabel?: string
}) {
  const current = encode(value.typeCode, value.ref)
  const known = links.choices.some((c) => encode(c.typeCode, c.ref) === current)

  const byType = new Map<string, LinkChoice[]>()
  for (const choice of links.choices) {
    const bucket = byType.get(choice.typeCode) ?? []
    bucket.push(choice)
    byType.set(choice.typeCode, bucket)
  }

  return (
    <select
      className={compact
        ? 'erp-input h-8 w-full text-erp-xs'
        : 'erp-input h-9 w-full text-erp-sm'}
      value={current}
      aria-label={ariaLabel}
      disabled={disabled || links.loading}
      onChange={(e) => onChange(decodeLinkValue(e.target.value))}
    >
      <option value="">{links.loading ? 'Loading…' : 'Not linked'}</option>
      {[...byType.entries()].map(([typeCode, choices]) => (
        <optgroup key={typeCode} label={linkTypeLabel(typeCode)}>
          {choices.map((choice) => (
            <option key={encode(choice.typeCode, choice.ref)}
              value={encode(choice.typeCode, choice.ref)}>
              {choice.label}
              {choice.secondaryLabel ? ` · ${choice.secondaryLabel}` : ''}
            </option>
          ))}
        </optgroup>
      ))}
      {/*
        A link the poll no longer declares is KEPT and shown, not silently
        dropped. Somebody removed it on step 3 after this question was written,
        and re-saving the question must not quietly detach a candidate from the
        option whose share is pushed onto their record.
      */}
      {current && !known && !links.loading && (
        <option value={current}>
          {value.typeCode} {value.ref} — no longer declared on this poll
        </option>
      )}
    </select>
  )
}
